import { useMemo, useState } from 'react';
import * as d3 from 'd3';
import { useD3Chart } from '../../hooks/useD3Chart';
import { useLazyVisualization } from '../../hooks/useLazyVisualization';
import { LazyVisualizationPlaceholder } from '../common/LazyVisualizationPlaceholder';

interface QQPlotProps {
  // 残差データ
  data: number[];
  // チャート幅
  width?: number;
  // チャート高さ
  height?: number;
  // 選択された行のインデックス
  selectedRows?: number[];
  // 点クリック時のコールバック
  onPointClick?: (dataIndices: number[], isAdditive?: boolean) => void;
}

// 定数
const MARGIN = { top: 45, right: 30, bottom: 50, left: 60 };

/**
 * QQプロット（Normal Q-Q Plot）コンポーネント
 * 残差の正規性を視覚的に確認するためのプロット
 */
export function QQPlot({
  data,
  width: defaultWidth = 400,
  height = 400,
  selectedRows = [],
  onPointClick
}: QQPlotProps) {
  // モード管理（pan or select）
  const [mode, setMode] = useState<'pan' | 'select'>('pan');

  // データサイズの計算（遅延レンダリングの判定に使用）
  const dataSize = data.length;

  // 遅延可視化フック（1,000行以上の場合はプレースホルダー表示）
  const {
    shouldRender,
    requestRender,
    isLoading,
    error,
    retry,
    notifyRenderComplete
  } = useLazyVisualization({
    initialRender: dataSize < 1000
  });

  // QQプロットのデータを計算
  const qqData = useMemo(() => {
    // 数値データのみをフィルタリング
    const validData = data
      .map((value, index) => ({ value, index }))
      .filter(d => typeof d.value === 'number' && !isNaN(d.value))
      .sort((a, b) => a.value - b.value);
    
    if (validData.length === 0) return [];
    
    const n = validData.length;
    
    // 理論分位数の計算（標準正規分布）
    const theoreticalQuantiles = validData.map((_, i) => {
      // プロット位置の計算（(i + 0.5) / n を使用）
      const p = (i + 0.5) / n;
      // 標準正規分布の逆関数（近似）
      return inverseNormalCDF(p);
    });
    
    return validData.map((d, i) => ({
      value: d.value,
      index: d.index,
      theoretical: theoreticalQuantiles[i]
    }));
  }, [data]);

  // useD3Chartで初期化とサイズ更新を管理
  const { containerRef, svgRef } = useD3Chart<{
    clipId: string;
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
    xScale: d3.ScaleLinear<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    xAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    yAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    xLabel: d3.Selection<SVGTextElement, unknown, null, undefined>;
    yLabel: d3.Selection<SVGTextElement, unknown, null, undefined>;
    referenceLine: d3.Selection<SVGLineElement, unknown, null, undefined>;
    brushGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
    tooltip: d3.Selection<HTMLDivElement, unknown, HTMLElement, undefined>;
    brush?: d3.BrushBehavior<unknown>;
    isClearing?: boolean;
  }>({
    defaultWidth,
    defaultHeight: height,
    monitorHeight: false,
    enableZoom: mode === 'pan',
    zoomOptions: {
      scaleExtent: [0.1, 10],
      extent: [[0, 0], [defaultWidth, height]],
      translateExtent: [[-MARGIN.left, -MARGIN.top], [defaultWidth + MARGIN.right, height + MARGIN.bottom]],
      filter: (event) => {
        if (event.type === 'wheel') {
          return (event as WheelEvent).ctrlKey || (event as WheelEvent).metaKey;
        }
        return !(event as MouseEvent).button;
      }
    },
    margin: MARGIN,
    setup: ({ svg, g }) => {
      // クリップパスを作成
      const clipId = `qq-clip-${Math.random().toString(36).substring(2, 11)}`;

      svg.append('defs')
        .append('clipPath')
        .attr('id', clipId)
        .append('rect');

      // チャートエリアのグループを作成（クリップパス適用）
      const chartGroup = g.append('g')
        .attr('clip-path', `url(#${clipId})`);

      // 軸グループを作成
      const xAxis = g.append('g')
        .attr('class', 'x-axis');

      const yAxis = g.append('g')
        .attr('class', 'y-axis');

      // 軸ラベルを作成
      const xLabel = g.append('text')
        .attr('class', 'x-label')
        .style('text-anchor', 'middle')
        .style('font-size', '12px')
        .text('Standard Normal Quantiles');

      const yLabel = g.append('text')
        .attr('class', 'y-label')
        .attr('transform', 'rotate(-90)')
        .style('text-anchor', 'middle')
        .style('font-size', '12px')
        .text('Standardized Residuals');

      // 参照線（45度線）を作成
      const referenceLine = chartGroup.append('line')
        .attr('class', 'reference-line')
        .style('stroke', '#ff0000')
        .style('stroke-width', 2)
        .style('stroke-dasharray', '5,5')
        .style('opacity', 0.7);

      // ブラシグループを作成（SVGの最上位に配置）
      const brushGroup = svg.append('g')
        .attr('class', 'brush');

      // ツールチップ作成
      d3.select('body').selectAll('.qq-tooltip').remove();
      const tooltip = d3.select('body').append('div')
        .attr('class', 'qq-tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('background-color', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '6px')
        .style('border-radius', '4px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '1000');

      // スケールを初期化
      const xScale = d3.scaleLinear();
      const yScale = d3.scaleLinear();

      return {
        clipId,
        chartGroup,
        xScale,
        yScale,
        xAxis,
        yAxis,
        xLabel,
        yLabel,
        referenceLine,
        brushGroup,
        tooltip
      };
    },
    render: ({ svg, g, refs, innerWidth, innerHeight, currentTransform }) => {
      if (!shouldRender) return;

      const { clipId, chartGroup, xScale, yScale, xAxis, yAxis, xLabel, yLabel, referenceLine, tooltip } = refs;

      // クリップパスのサイズを更新
      svg.select(`#${clipId} rect`)
        .attr('width', innerWidth)
        .attr('height', innerHeight);

      // X軸の位置を更新
      xAxis.attr('transform', `translate(0,${innerHeight})`);

      // ラベルの位置を更新
      xLabel.attr('transform', `translate(${innerWidth / 2}, ${innerHeight + MARGIN.bottom})`);
      yLabel
        .attr('y', 0 - MARGIN.left)
        .attr('x', 0 - (innerHeight / 2))
        .attr('dy', '1em');

      // スケールのrangeを更新
      xScale.range([0, innerWidth]);
      yScale.range([innerHeight, 0]);

      // データがない場合は早期リターン
      if (qqData.length === 0) {
        notifyRenderComplete();
        return;
      }

      // データ範囲を計算してドメイン設定
      const xExtent = d3.extent(qqData, d => d.theoretical) as [number, number];
      const yExtent = d3.extent(qqData, d => d.value) as [number, number];

      if (!xExtent || !yExtent) {
        notifyRenderComplete();
        return;
      }

      // 少しマージンを追加
      const xPadding = (xExtent[1] - xExtent[0]) * 0.1;
      const yPadding = (yExtent[1] - yExtent[0]) * 0.1;

      xScale.domain([xExtent[0] - xPadding, xExtent[1] + xPadding]);
      yScale.domain([yExtent[0] - yPadding, yExtent[1] + yPadding]);

      // ズーム適用後のスケールを計算（currentTransformを使用）
      const zoomTransform = currentTransform || d3.zoomIdentity;
      const visibleXScale = zoomTransform.rescaleX(xScale);
      const visibleYScale = zoomTransform.rescaleY(yScale);

      // 軸を更新
      xAxis.call(d3.axisBottom(visibleXScale).ticks(5))
        .selectAll('text')
        .style('font-size', '11px')
        .style('fill', '#666');

      yAxis.call(d3.axisLeft(visibleYScale).ticks(5))
        .selectAll('text')
        .style('font-size', '11px')
        .style('fill', '#666');

      g.selectAll('.x-axis path, .y-axis path, .x-axis line, .y-axis line')
        .style('stroke', '#ccc');

      // 参照線（45度線）を更新
      const visibleXDomain = visibleXScale.domain() as [number, number];
      const visibleYDomain = visibleYScale.domain() as [number, number];
      const minVal = Math.max(visibleXDomain[0], visibleYDomain[0]);
      const maxVal = Math.min(visibleXDomain[1], visibleYDomain[1]);

      referenceLine
        .attr('x1', visibleXScale(minVal))
        .attr('y1', visibleYScale(minVal))
        .attr('x2', visibleXScale(maxVal))
        .attr('y2', visibleYScale(maxVal));

      // 点を描画
      const points = chartGroup.selectAll<SVGCircleElement, typeof qqData[0]>('.qq-point')
        .data(qqData, d => String(d.index));

      points.exit().remove();

      const pointsEnter = points.enter().append('circle')
        .attr('class', 'qq-point');

      const allPoints = points.merge(pointsEnter);

      allPoints
        .attr('cx', d => visibleXScale(d.theoretical))
        .attr('cy', d => visibleYScale(d.value))
        .attr('r', 3)
        .style('fill', d => selectedRows.includes(d.index) ? '#ff6b6b' : '#4285f4')
        .style('stroke', '#fff')
        .style('stroke-width', 1)
        .style('opacity', d => selectedRows.length > 0 ? (selectedRows.includes(d.index) ? 0.9 : 0.3) : 0.7)
        .style('cursor', onPointClick ? 'pointer' : 'default');

      // イベントハンドラー
      allPoints
        .on('mouseover', function(this: SVGCircleElement, event, d) {
          d3.select(this).attr('r', 5);
          tooltip
            .style('visibility', 'visible')
            .html(`Index: ${d.index}<br/>Theoretical: ${d.theoretical.toFixed(3)}<br/>Value: ${d.value.toFixed(3)}`)
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 28) + 'px');
        })
        .on('mouseout', function(this: SVGCircleElement) {
          d3.select(this).attr('r', 3);
          tooltip.style('visibility', 'hidden');
        })
        .on('click', function(event, d) {
          if (onPointClick) {
            const isAdditive = event.metaKey || event.ctrlKey;
            onPointClick([d.index], isAdditive);
          }
        });

      notifyRenderComplete();
    },
    dependencies: [shouldRender, qqData, selectedRows, mode, onPointClick]
  });

  const showChart = shouldRender && !error;

  return (
    <div ref={containerRef} style={{ width: '100%', height: height, overflow: 'hidden' }}>
      {/* 遅延レンダリング：プレースホルダー表示 */}
      {(!shouldRender || error) && (
        <LazyVisualizationPlaceholder
          dataSize={dataSize}
          onView={requestRender}
          visualizationType="Q-Q plot"
          height={height}
          isLoading={isLoading}
          error={error}
          onRetry={retry}
        />
      )}

      {/* SVGチャート：常にDOMに存在させ、display制御で表示/非表示 */}
      <div style={{ position: 'relative', display: showChart ? 'block' : 'none' }}>
        {/* SVGチャート */}
        <svg
          ref={svgRef}
          style={{
            border: '1px solid #e9ecef',
            borderRadius: '4px',
            background: 'white',
            cursor: mode === 'pan' ? 'grab' : 'crosshair',
            display: 'block',
            maxWidth: '100%'
          }}
        />

        {/* オーバーレイボタン */}
        <div style={{ 
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: 'flex',
        gap: '4px',
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '4px',
        padding: '2px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <button
          onClick={() => setMode('pan')}
          title="パンモード"
          style={{
            width: '28px',
            height: '28px',
            padding: '0',
            border: 'none',
            borderRadius: '4px',
            background: mode === 'pan' ? 'rgba(66, 133, 244, 0.2)' : 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (mode !== 'pan') {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (mode !== 'pan') {
              e.currentTarget.style.background = 'transparent';
            }
          }}
        >
          {/* パンアイコン（移動矢印） */}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 1L6 3h1.5v4.5H3V6L1 8l2 2v-1.5h4.5V13H6l2 2 2-2H8.5V8.5H13V10l2-2-2-2v1.5H8.5V3H10L8 1z" 
              fill={mode === 'pan' ? '#4285f4' : '#666'} />
          </svg>
        </button>
        <button
          onClick={() => setMode('select')}
          title="選択モード"
          style={{
            width: '28px',
            height: '28px',
            padding: '0',
            border: 'none',
            borderRadius: '4px',
            background: mode === 'select' ? 'rgba(66, 133, 244, 0.2)' : 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (mode !== 'select') {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (mode !== 'select') {
              e.currentTarget.style.background = 'transparent';
            }
          }}
        >
          {/* 選択アイコン（矩形選択） */}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="2" width="12" height="12" rx="1" 
              stroke={mode === 'select' ? '#4285f4' : '#666'} 
              strokeWidth="1.5" 
              strokeDasharray="3 2"
              fill="none" />
          </svg>
        </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 標準正規分布の逆関数（累積分布関数の逆関数）
 * Beasley-Springer-Moro アルゴリズムの簡易実装
 */
function inverseNormalCDF(p: number): number {
  if (p <= 0) return -Infinity;
  if (p >= 1) return Infinity;
  
  // 係数（Beasley-Springer-Moro）
  const a = [
    2.50662823884,
    -18.61500062529,
    41.39119773534,
    -25.44106049637
  ];
  
  const b = [
    -8.47351093090,
    23.08336743743,
    -21.06224101826,
    3.13082909833
  ];
  
  const c = [
    0.3374754822726147,
    0.9761690190917186,
    0.1607979714918209,
    0.0276438810333863,
    0.0038405729373609,
    0.0003951896511919,
    0.0000321767881768,
    0.0000002888167364,
    0.0000003960315187
  ];
  
  const y = p - 0.5;
  
  if (Math.abs(y) < 0.42) {
    // Central region
    const r = y * y;
    const x = y * (((a[3] * r + a[2]) * r + a[1]) * r + a[0]) /
              ((((b[3] * r + b[2]) * r + b[1]) * r + b[0]) * r + 1);
    return x;
  } else {
    // Tail region
    let r = p;
    if (y > 0) r = 1 - p;
    r = Math.log(-Math.log(r));
    
    let x = c[0];
    for (let i = 1; i < 9; i++) {
      x = x * r + c[i];
    }
    
    if (y < 0) x = -x;
    return x;
  }
}